"use client"

import { forwardRef, useEffect, useImperativeHandle, useRef } from "react"

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
const SCRIPT_ID = "cf-turnstile-script"

interface TurnstileApi {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string
      callback?: (token: string) => void
      "expired-callback"?: () => void
      "error-callback"?: () => void
      theme?: "light" | "dark" | "auto"
      size?: "normal" | "flexible" | "compact" | "invisible"
      appearance?: "always" | "execute" | "interaction-only"
      action?: string
    },
  ) => string
  reset: (widgetId?: string) => void
  remove: (widgetId?: string) => void
}

declare global {
  interface Window {
    turnstile?: TurnstileApi
    __turnstileLoaderPromise?: Promise<void>
  }
}

function loadScript(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve()
  if (window.turnstile) return Promise.resolve()
  if (window.__turnstileLoaderPromise) return window.__turnstileLoaderPromise

  window.__turnstileLoaderPromise = new Promise<void>((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null
    if (existing) {
      existing.addEventListener("load", () => resolve())
      existing.addEventListener("error", () => reject(new Error("turnstile script failed to load")))
      return
    }
    const script = document.createElement("script")
    script.id = SCRIPT_ID
    script.src = SCRIPT_SRC
    script.async = true
    script.defer = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error("turnstile script failed to load"))
    document.head.appendChild(script)
  })

  return window.__turnstileLoaderPromise
}

export interface TurnstileHandle {
  reset: () => void
}

interface TurnstileWidgetProps {
  onVerify: (token: string) => void
  onExpire?: () => void
  onError?: () => void
  theme?: "light" | "dark" | "auto"
  size?: "normal" | "flexible" | "compact"
  className?: string
}

export const TurnstileWidget = forwardRef<TurnstileHandle, TurnstileWidgetProps>(
  function TurnstileWidget(
    { onVerify, onExpire, onError, theme = "auto", size = "flexible", className },
    ref,
  ) {
    const containerRef = useRef<HTMLDivElement | null>(null)
    const widgetIdRef = useRef<string | null>(null)
    const onVerifyRef = useRef(onVerify)
    const onExpireRef = useRef(onExpire)
    const onErrorRef = useRef(onError)

    useEffect(() => {
      onVerifyRef.current = onVerify
      onExpireRef.current = onExpire
      onErrorRef.current = onError
    }, [onVerify, onExpire, onError])

    useImperativeHandle(ref, () => ({
      reset: () => {
        if (window.turnstile && widgetIdRef.current) {
          window.turnstile.reset(widgetIdRef.current)
        }
      },
    }))

    useEffect(() => {
      const sitekey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY
      if (!sitekey) {
        console.error("[turnstile] NEXT_PUBLIC_TURNSTILE_SITE_KEY missing")
        return
      }

      let cancelled = false

      loadScript()
        .then(() => {
          if (cancelled) return
          if (!containerRef.current || !window.turnstile) return
          if (widgetIdRef.current) return
          widgetIdRef.current = window.turnstile.render(containerRef.current, {
            sitekey,
            theme,
            size,
            callback: (token) => onVerifyRef.current(token),
            "expired-callback": () => onExpireRef.current?.(),
            "error-callback": () => onErrorRef.current?.(),
          })
        })
        .catch((err) => {
          console.error("[turnstile]", err)
        })

      return () => {
        cancelled = true
        if (window.turnstile && widgetIdRef.current) {
          window.turnstile.remove(widgetIdRef.current)
          widgetIdRef.current = null
        }
      }
    }, [theme, size])

    return <div ref={containerRef} className={className} />
  },
)
